import { supabase } from "@/integrations/supabase/client";

/**
 * The Espelho, mini mirrors and Guide share the current kind='mirror' row.
 * Tutor visits always create isolated kind='language_tutor' rows. The cache
 * belongs to one authenticated owner and one generation: an old lookup must
 * never restore a conversation after logout, account switch or explicit reset.
 */
const RECENT_WINDOW_MS = 6 * 60 * 60 * 1000;
let ownerId: string | null = null;
let cachedSessionId: string | null = null;
let generation = 0;
let authStateSeen = false;
let authenticatedOwnerId: string | null = null;
let skipRecentLookup = false;
let inFlight: { ownerId: string; generation: number; promise: Promise<string> } | null = null;

export class MirrorSessionChangedError extends Error {
  constructor() {
    super("Mirror session changed");
    this.name = "MirrorSessionChangedError";
  }
}

function invalidate(nextOwner: string | null, forceNew = false): void {
  generation++;
  ownerId = nextOwner;
  cachedSessionId = null;
  inFlight = null;
  skipRecentLookup = forceNew;
}

function bindOwner(userId: string): void {
  if (!userId) throw new Error("User not authenticated");
  if (authStateSeen && authenticatedOwnerId !== userId) throw new MirrorSessionChangedError();
  if (ownerId !== userId) invalidate(userId, skipRecentLookup && ownerId === null);
}

function assertCurrent(userId: string, requestGeneration: number): void {
  if (ownerId !== userId || generation !== requestGeneration) throw new MirrorSessionChangedError();
}

// This module is a singleton, like the Supabase client. Auth callbacks only
// invalidate synchronous state; they never make an auth/network call inside
// Supabase's auth lock. Same-owner token refresh does not fragment a session.
supabase.auth.onAuthStateChange((_event, session) => {
  const nextOwner = session?.user?.id ?? null;
  authStateSeen = true;
  authenticatedOwnerId = nextOwner;
  if (nextOwner !== ownerId) invalidate(nextOwner);
});

export interface MirrorSessionOwnerGuard { accessToken: string; assertCurrent: () => void }
export async function getOrCreateMirrorSession(userId: string, guard?: MirrorSessionOwnerGuard): Promise<string> {
  guard?.assertCurrent();
  bindOwner(userId);
  if (cachedSessionId) return cachedSessionId;
  if (inFlight?.ownerId === userId && inFlight.generation === generation) { const result = await inFlight.promise; guard?.assertCurrent(); return result; }

  const requestGeneration = generation;
  const forceNew = skipRecentLookup;
  const promise = (async () => {
    if (!forceNew) {
      const query = supabase
        .from("mirror_sessions")
        .select("id, user_id, kind")
        .eq("user_id", userId)
        .eq("kind", "mirror")
        .gte("updated_at", new Date(Date.now() - RECENT_WINDOW_MS).toISOString())
        .order("updated_at", { ascending: false })
        .order("id", { ascending: false })
        .limit(1);
      const { data: existing, error } = await (guard ? query.setHeader("Authorization", "Bearer " + guard.accessToken) : query).maybeSingle();
      guard?.assertCurrent();
      assertCurrent(userId, requestGeneration);
      if (error) throw error;
      if (existing) {
        if (!existing.id || existing.user_id !== userId || existing.kind !== "mirror") {
          throw new Error("Mirror session ownership mismatch");
        }
        cachedSessionId = existing.id;
        return existing.id;
      }
    }

    assertCurrent(userId, requestGeneration);
    guard?.assertCurrent();
    const query = supabase.from("mirror_sessions").insert({ user_id: userId, kind: "mirror" }).select("id, user_id, kind");
    const { data: created, error } = await (guard ? query.setHeader("Authorization", "Bearer " + guard.accessToken) : query).single();
    guard?.assertCurrent();
    assertCurrent(userId, requestGeneration);
    if (error || !created?.id) throw error || new Error("Failed to create mirror session");
    if (created.user_id !== userId || created.kind !== "mirror") throw new Error("Mirror session ownership mismatch");
    cachedSessionId = created.id;
    skipRecentLookup = false;
    return created.id;
  })();
  inFlight = { ownerId: userId, generation: requestGeneration, promise };
  try {
    const result = await promise; guard?.assertCurrent(); return result;
  } finally {
    // An older completion cannot clear a newer owner's single-flight guard.
    if (inFlight?.promise === promise) inFlight = null;
  }
}

/** One new isolated lesson per visit; never reads/writes the shared cache. */
export async function createIsolatedMirrorSession(userId: string): Promise<string> {
  bindOwner(userId);
  const requestGeneration = generation;
  const { data: created, error } = await supabase
    .from("mirror_sessions")
    .insert({ user_id: userId, kind: "language_tutor" })
    .select("id, user_id, kind")
    .single();
  assertCurrent(userId, requestGeneration);
  if (error || !created?.id) throw error || new Error("Failed to create isolated tutor session");
  if (created.user_id !== userId || created.kind !== "language_tutor") throw new Error("Mirror session ownership mismatch");
  return created.id;
}

/** Explicit new conversation skips the recent-row fallback on the next send. */
export function resetMirrorSession(): void {
  invalidate(ownerId, true);
}

/** History selection must name the owner, after verifying its row. */
export function setMirrorSession(sessionId: string | null, userId?: string): void {
  // Preserve the call signature for existing integrations, but an unowned id
  // cannot become shared state. The history UI always supplies its verified owner.
  if (userId && authStateSeen && userId !== authenticatedOwnerId) throw new MirrorSessionChangedError();
  invalidate(userId ?? ownerId);
  if (userId && sessionId) cachedSessionId = sessionId;
}

export function peekMirrorSession(userId?: string): string | null {
  return userId && userId === ownerId ? cachedSessionId : null;
}
